import { GRAVITY, MAX_WIND, MIN_ANGLE, MAX_ANGLE, MIN_POWER, MAX_POWER } from './scorched-earth.constants';
import { isTerrainAt } from './scorched-earth.terrain';
import { Projectile, Vec2, WeaponId } from './scorched-earth.types';
import { WEAPONS } from './scorched-earth.constants';

export function clampAngle(angle: number): number {
	return Math.max(MIN_ANGLE, Math.min(MAX_ANGLE, angle));
}

export function clampPower(power: number): number {
	return Math.max(MIN_POWER, Math.min(MAX_POWER, power));
}

export function degToRad(deg: number): number {
	return (deg * Math.PI) / 180;
}

export function launchVelocity(
	angleDeg: number,
	power: number,
	facingRight: boolean,
	weaponId: WeaponId,
): Vec2 {
	const weapon = WEAPONS[weaponId];
	const rad = degToRad(angleDeg);
	const speed = power * weapon.projectileSpeed;
	const dir = facingRight ? 1 : -1;
	return {
		x: Math.cos(rad) * speed * dir,
		y: -Math.sin(rad) * speed,
	};
}

export function stepProjectile(
	projectile: Projectile,
	terrain: Float32Array,
	wind: number,
	dt = 1,
): Projectile {
	const weapon = WEAPONS[projectile.weaponId];
	const next: Projectile = {
		...projectile,
		x: projectile.x + projectile.vx * dt,
		y: projectile.y + projectile.vy * dt,
		vx: projectile.vx + (wind / MAX_WIND) * 0.018 * weapon.windScale * dt,
		vy: projectile.vy + GRAVITY * weapon.gravityScale * dt,
		age: projectile.age + dt,
		trail: [...projectile.trail, { x: projectile.x, y: projectile.y }].slice(-24),
	};
	return next;
}

export function projectileHitsTerrain(
	projectile: Projectile,
	terrain: Float32Array,
): boolean {
	return isTerrainAt(terrain, projectile.x, projectile.y);
}

export function projectileOutOfBounds(
	projectile: Projectile,
	worldWidth: number,
	worldHeight: number,
): boolean {
	return (
		projectile.x < -20 ||
		projectile.x > worldWidth + 20 ||
		projectile.y > worldHeight + 40 ||
		projectile.age > 900
	);
}

export function distance(a: Vec2, b: Vec2): number {
	const dx = a.x - b.x;
	const dy = a.y - b.y;
	return Math.sqrt(dx * dx + dy * dy);
}

export function damageAtDistance(maxDamage: number, blastRadius: number, dist: number): number {
	if (dist >= blastRadius) {
		return 0;
	}
	const t = 1 - dist / blastRadius;
	return Math.round(maxDamage * t * t);
}

export interface TrajectoryPreviewOptions {
	angle: number;
	power: number;
	startX: number;
	startY: number;
	facingRight: boolean;
	weaponId: WeaponId;
	wind: number;
	worldWidth: number;
	worldHeight: number;
	terrain: Float32Array;
	seed: number;
	maxSteps?: number;
}

/** Approximate dotted trajectory — stops early and adds slight jitter so it is not a perfect solver. */
export function buildTrajectoryPreview(opts: TrajectoryPreviewOptions): Vec2[] {
	const {
		angle,
		power,
		startX,
		startY,
		facingRight,
		weaponId,
		wind,
		worldWidth,
		worldHeight,
		terrain,
		seed,
		maxSteps = 42,
	} = opts;

	const vel = launchVelocity(angle, power, facingRight, weaponId);
	let x = startX;
	let y = startY;
	let vx = vel.x;
	let vy = vel.y;
	const weapon = WEAPONS[weaponId];
	const points: Vec2[] = [{ x, y }];
	const jitter = ((seed % 17) - 8) * 0.15;

	for (let i = 0; i < maxSteps; i++) {
		x += vx;
		y += vy;
		vx += (wind / MAX_WIND) * 0.018 * weapon.windScale;
		vy += GRAVITY * weapon.gravityScale;
		if (i % 3 === 0) {
			points.push({
				x: x + Math.sin(i + seed) * jitter,
				y: y + Math.cos(i * 0.7 + seed) * jitter * 0.5,
			});
		}
		if (isTerrainAt(terrain, x, y) || x < 0 || x > worldWidth || y > worldHeight) {
			break;
		}
	}
	return points;
}

export function shouldSplitMirv(projectile: Projectile): boolean {
	if (projectile.weaponId !== 'mirv' || projectile.splitDone) {
		return false;
	}
	return projectile.age > 8 && projectile.vy > 0;
}

export function createMirvWarheads(
	projectile: Projectile,
	nextId: number,
): { projectiles: Projectile[]; nextId: number } {
	const weapon = WEAPONS.mirv;
	const count = weapon.splits ?? 5;
	const spread = weapon.splitSpreadDeg ?? 18;
	const baseAngle = Math.atan2(-projectile.vy, projectile.vx) * (180 / Math.PI);
	const speed = Math.sqrt(projectile.vx ** 2 + projectile.vy ** 2) * 0.72;
	const projectiles: Projectile[] = [];
	let id = nextId;

	for (let i = 0; i < count; i++) {
		const offset = (i - (count - 1) / 2) * spread;
		const rad = degToRad(baseAngle + offset);
		projectiles.push({
			id: id++,
			x: projectile.x,
			y: projectile.y,
			vx: Math.cos(rad) * speed,
			vy: -Math.sin(rad) * speed,
			ownerId: projectile.ownerId,
			weaponId: 'missile',
			age: 0,
			splitDone: true,
			trail: [],
		});
	}
	return { projectiles, nextId: id };
}
